import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GameCanvas, SOLO_SECONDS } from "./components/GameCanvas";
import { audio } from "./audio";
import { App as NativeApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { adsAvailable, initAds, maybeShowInterstitial, showRewarded } from "./ads";
import type { GameResult, GameStats } from "./game/GameEngine";
import type { ArenaStatus } from "./game/ArenaClient";
import { SkinPreview, useSkinThumbnails } from "./components/SkinPreview";
import { RARITY_COLOR, skinById } from "./game/skins";
import {
  SKINS,
  buySkin,
  equipSkin as equipOwnedSkin,
  ownsSkin,
  claimMission,
  missionKind,
  loadProfile,
  recordRun,
  saveProfile,
  type Profile,
} from "./profile";
import {
  initI18n,
  applyTranslations,
  onLangChange,
  getLang,
  getSortedLangs,
  setLang,
  t,
} from "./i18n";

// Device-Settings-First: i18n aktivieren bevor die App rendert.
initI18n();
applyTranslations();
onLangChange(() => applyTranslations());

type Screen = "home" | "game" | "end" | "skins" | "missions";
type Mode = "solo" | "arena";

const EMPTY_STATS: GameStats = { score: 0, eaten: 0, radius: 2.4, time: 75, combo: 0 };

export default function App() {
  const [profile, setProfile] = useState<Profile>(() => loadProfile());
  const [screen, setScreen] = useState<Screen>("home");
  const [mode, setMode] = useState<Mode>("solo");
  const [stats, setStats] = useState<GameStats>(EMPTY_STATS);
  const [result, setResult] = useState<{ score: number; eaten: number; coins: number; rank?: number; total?: number }>();
  const [arenaStatus, setArenaStatus] = useState<{ status: ArenaStatus; message?: string }>({ status: "idle" });
  const skin = useMemo(() => skinById(profile.equippedSkin), [profile.equippedSkin]);
  const [shopSkin, setShopSkin] = useState(profile.equippedSkin);

  const [doubled, setDoubled] = useState(false);

  useEffect(() => { void initAds(); }, []);

  // Android hardware back: leave sub-screens, exit from home.
  const screenRef = useRef(screen);
  screenRef.current = screen;
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const handle = NativeApp.addListener("backButton", () => {
      if (screenRef.current === "home") void NativeApp.exitApp();
      else setScreen("home");
    });
    return () => { void handle.then((h) => h.remove()); };
  }, []);

  useEffect(() => {
    if (screen === "home" || screen === "skins" || screen === "missions") void audio.playMusic("menu");
    if (screen === "game") void audio.playMusic("play");
    if (screen === "end") { audio.stopMusic(); void audio.playSfx("end", 0.45); }
  }, [screen]);

  const start = (nextMode: Mode) => {
    audio.ensure();
    void audio.playSfx("click", 0.4);
    setMode(nextMode);
    setStats({ ...EMPTY_STATS, time: nextMode === "arena" ? 120 : SOLO_SECONDS });
    setResult(undefined);
    setDoubled(false);
    setArenaStatus({ status: "idle" });
    setScreen("game");
  };

  const finish = useCallback((gameResult: GameResult, arena?: { rank: number; coins: number }) => {
    setProfile((current) => {
      const rank = arena?.rank ?? gameResult.rank;
      const soloCoins = Math.floor(gameResult.score / 100) + (rank === 1 ? 100 : rank === 2 ? 50 : rank === 3 ? 25 : 0);
      const coins = arena?.coins ?? soloCoins;
      const next = recordRun(current, mode, gameResult.score, gameResult.eaten, coins, rank);
      setResult({ score: gameResult.score, eaten: gameResult.eaten, coins, rank, total: gameResult.total });
      return next;
    });
    setScreen("end");
  }, [mode]);

  const doubleCoins = async () => {
    if (!result || doubled) return;
    audio.stopMusic();
    const earned = await showRewarded();
    if (!earned) return;
    setDoubled(true);
    setResult({ ...result, coins: result.coins * 2 });
    setProfile((current) => {
      const next = { ...current, coins: current.coins + result.coins };
      saveProfile(next);
      return next;
    });
  };

  const updateStats = useCallback((next: GameStats) => setStats(next), []);
  const updateArenaStatus = useCallback((status: ArenaStatus, message?: string) => setArenaStatus({ status, message }), []);

  const openShop = () => {
    setShopSkin(profile.equippedSkin);
    setScreen("skins");
  };

  const shopAction = (id: string) => {
    if (ownsSkin(profile, id)) {
      setProfile(equipOwnedSkin(profile, id));
      void audio.playSfx("click", 0.4);
      return;
    }
    const next = buySkin(profile, id);
    if (next !== profile) {
      setProfile(next);
      void audio.playSfx("eat_big", 0.45);
    }
  };

  const claim = (id: string) => setProfile((current) => claimMission(current, id));

  /** Cycle durch die sortierten Sprachen (Phase 0.5). */
  const cycleLang = () => {
    const sorted = getSortedLangs();
    const idx = sorted.indexOf(getLang());
    const next = sorted[(idx + 1) % sorted.length] ?? sorted[0];
    if (next) setLang(next);
  };

  return (
    <main className={`app screen-${screen}`}>
      {screen === "home" && (
        <section className="home-screen">
          <div className="ambient ambient-one" />
          <div className="ambient ambient-two" />
          <header className="brand">
            <button className="hero-hole" onClick={openShop} aria-label={t("home.skins")}>
              <SkinPreview skinId={skin.id} className="hero-canvas" />
              <span className="hero-skin-name" style={{ color: RARITY_COLOR[skin.rarity] }}>{skin.label}</span>
            </button>
            <p className="eyebrow">{t("home.eyebrow")}</p>
            <h1>{t("home.title.line1")}<br /><strong>{t("home.title.line2")}</strong></h1>
            <p className="tagline" dangerouslySetInnerHTML={{ __html: t("home.tagline").replace(/\n/g, "<br />") }} />
          </header>

          <div className="home-card">
            <div className="currency"><span>◈</span><b>{profile.coins.toLocaleString()}</b></div>
            <button className="primary-button" onClick={() => start("solo")}>
              <span className="button-icon">▶</span>
              <span><small>{t("home.solo.eyebrow")}</small>{t("home.solo.title")}</span>
            </button>
            <button className="arena-button" onClick={() => start("arena")}>
              <span className="button-icon">◉</span>
              <span><small>{t("home.arena.eyebrow")}</small>{t("home.arena.title")}</span>
            </button>
            <div className="secondary-actions">
              <button onClick={openShop}><span>◌</span>{t("home.skins")}</button>
              <button onClick={() => setScreen("missions")}><span>✓</span>{t("home.missions")}</button>
              <button onClick={cycleLang} aria-label={t("home.lang")}>🌐 {getLang().toUpperCase()}</button>
            </div>
          </div>

          <footer className="home-footer">
            <span>{t("home.footer.best")} <b>{profile.bestSolo.toLocaleString()}</b></span>
            <span>{t("home.footer.runs")} <b>{profile.runs}</b></span>
            <span>{t("home.footer.eaten")} <b>{profile.eaten}</b></span>
          </footer>
        </section>
      )}

      {screen === "game" && (
        <section className="game-screen">
          <GameCanvas
            mode={mode}
            skinColor={skin.color}
            skinId={skin.id}
            onStats={updateStats}
            onFinish={finish}
            onArenaStatus={updateArenaStatus}
          />
          <div className="top-hud">
            <div className="score-panel"><small>{t("game.score")}</small><b>{Math.floor(stats.score).toLocaleString()}</b></div>
            <div className={`timer ${stats.time <= 10 ? "urgent" : ""}`}><span>{Math.ceil(stats.time)}</span></div>
            <div className="size-panel"><small>{t("game.size")}</small><b>Ø {(stats.radius * 2).toFixed(1)}</b></div>
          </div>
          <div className="game-meta">
            <span>{stats.eaten} {t("game.eatenSuffix")}</span>
            {stats.combo > 2 && <strong>COMBO ×{stats.combo}</strong>}
          </div>
          {mode === "arena" && arenaStatus.status !== "playing" && (
            <div className="arena-status">
              <div className="spinner" />
              <b>{arenaStatus.message ?? (arenaStatus.status === "connecting" ? t("game.arena.connecting") : t("game.arena.searching"))}</b>
            </div>
          )}
          {stats.leaders && (
            <ol className="leaderboard">
              <li className="board-head"><span>{t("game.leaderboard")}</span><b>Ø</b><em>★</em></li>
              {stats.leaders.map((entry, index) => (
                <li key={entry.name} className={entry.me ? "me" : ""}>
                  <i style={{ background: entry.color ?? "#8da0bd" }} />
                  <span>{index + 1}. {entry.name}</span>
                  <b>{entry.size.toFixed(1)}</b>
                  <em>{Math.floor(entry.score).toLocaleString()}</em>
                </li>
              ))}
              {stats.rank && stats.rank > stats.leaders.length && (
                <li className="me">
                  <span>{stats.rank}. {t("game.you")}</span>
                  <b>{(stats.radius * 2).toFixed(1)}</b>
                  <em>{Math.floor(stats.score).toLocaleString()}</em>
                </li>
              )}
            </ol>
          )}
          {stats.feed && <div key={stats.feed.id} className="kill-feed">{stats.feed.text}</div>}
          {stats.respawnIn !== undefined && (
            <div className="respawn-overlay"><b>{t("game.swallowed")}</b><span>{t("game.respawn", { n: Math.ceil(stats.respawnIn) })}</span></div>
          )}
          <div className="control-hint">{t("game.controlHint")}</div>
          <button className="quit-button" onClick={() => setScreen("home")} aria-label={t("game.quit")}>×</button>
        </section>
      )}

      {screen === "end" && result && (
        <section className="modal-screen">
          <div className="result-card">
            <p className="eyebrow">{t(mode === "arena" ? "result.eyebrow.arena" : "result.eyebrow.solo")}</p>
            <div className="result-hole-3d"><SkinPreview skinId={skin.id} className="hero-canvas" /></div>
            {result.rank && <div className="rank">{t("result.rank", { n: result.rank })}{result.total ? ` / ${result.total}` : ""}</div>}
            <h2>{result.score.toLocaleString()}</h2>
            <p>{t("result.score")}</p>
            <div className="result-grid">
              <span><small>{t("result.objects")}</small><b>{result.eaten}</b></span>
              <span><small>{t("result.coins")}</small><b>+{result.coins}</b></span>
              <span><small>{t("result.best")}</small><b>{(mode === "solo" ? profile.bestSolo : profile.bestArena).toLocaleString()}</b></span>
            </div>
            {adsAvailable() && !doubled && result.coins > 0 && (
              <button className="reward-button" onClick={() => void doubleCoins()}>▶ {t("result.double", { n: result.coins })}</button>
            )}
            <button className="primary-button" onClick={() => { void maybeShowInterstitial(); start(mode); }}>{t("result.playAgain")}</button>
            <button className="text-button" onClick={() => { void maybeShowInterstitial(); setScreen("home"); }}>{t("result.back")}</button>
          </div>
        </section>
      )}

      {screen === "skins" && <SkinShop
        profile={profile}
        selected={shopSkin}
        onSelect={(id) => { setShopSkin(id); void audio.playSfx("click", 0.25); }}
        onAction={shopAction}
        onBack={() => setScreen("home")}
      />}

      {screen === "missions" && (
        <section className="collection-screen">
          <ScreenHeader title={t("missions.title")} onBack={() => setScreen("home")} />
          <div className="mission-list">
            {profile.missions.map((mission) => {
              const complete = mission.progress >= mission.target;
              return (
                <article key={mission.id} className={complete ? "complete" : ""}>
                  <div>
                    <b>{t(`missions.${missionKind(mission.id)}`, { n: mission.target.toLocaleString() })}</b>
                    <span className="progress-track"><i style={{ width: `${Math.min(100, mission.progress / mission.target * 100)}%` }} /></span>
                    <small>{mission.progress.toLocaleString()} / {mission.target.toLocaleString()}</small>
                  </div>
                  <button disabled={!complete || mission.claimed} onClick={() => claim(mission.id)}>
                    {mission.claimed ? t("missions.done") : t("missions.claim", { n: mission.reward })}
                  </button>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
}

function ScreenHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className="screen-header">
      <button onClick={onBack} aria-label={t("header.back")}>←</button>
      <div><p className="eyebrow">{t("header.brand")}</p><h2>{title}</h2></div>
      <span />
    </header>
  );
}

function SkinShop({ profile, selected, onSelect, onAction, onBack }: {
  profile: Profile;
  selected: string;
  onSelect: (id: string) => void;
  onAction: (id: string) => void;
  onBack: () => void;
}) {
  const thumbs = useSkinThumbnails();
  const current = skinById(selected);
  const owned = ownsSkin(profile, current.id);
  const equipped = profile.equippedSkin === current.id;
  const affordable = profile.coins >= current.price;
  return (
    <section className="collection-screen shop-screen">
      <ScreenHeader title={t("skins.title")} onBack={onBack} />
      <div className="shop-stage" style={{ "--rarity": RARITY_COLOR[current.rarity] } as React.CSSProperties}>
        <SkinPreview skinId={current.id} className="shop-canvas" />
        <div className="shop-info">
          <span className="rarity-chip">{t(`rarity.${current.rarity}`)}</span>
          <h3>{current.label}</h3>
          <div className="currency small"><span>◈</span><b>{profile.coins.toLocaleString()}</b></div>
        </div>
        <button
          className={`shop-action ${equipped ? "equipped" : owned ? "owned" : affordable ? "buy" : "locked"}`}
          disabled={equipped || (!owned && !affordable)}
          onClick={() => onAction(current.id)}
        >
          {equipped ? t("skins.equipped") : owned ? t("skins.equip") : affordable
            ? t("shop.buy", { n: current.price.toLocaleString() })
            : t("shop.missing", { n: (current.price - profile.coins).toLocaleString() })}
        </button>
      </div>
      <div className="skin-grid">
        {SKINS.map((entry) => {
          const has = ownsSkin(profile, entry.id);
          return (
            <button
              key={entry.id}
              className={`skin-card ${selected === entry.id ? "selected" : ""} ${has ? "owned" : "locked"}`}
              style={{ "--rarity": RARITY_COLOR[entry.rarity], "--skin": entry.color } as React.CSSProperties}
              onClick={() => onSelect(entry.id)}
            >
              {thumbs.get(entry.id)
                ? <img className="skin-thumb" src={thumbs.get(entry.id)} alt="" draggable={false} />
                : <span className="skin-preview" />}
              <b>{entry.label}</b>
              <small>
                {profile.equippedSkin === entry.id ? t("skins.equipped") : has ? t("shop.owned") : <>◈ {entry.price.toLocaleString()}</>}
              </small>
            </button>
          );
        })}
      </div>
    </section>
  );
}
